// "I saw a free spot" — a spot that is free RIGHT NOW, so there is no timer to
// pick: it goes live immediately with a short automatic expiry.

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Eye, Loader2, Camera } from "lucide-react";
import { useBackdropDismiss } from "../lib/useBackdropDismiss";

const TYPES = [
  { key: "normal", label: "Normal" },
  { key: "blue_badge", label: "Blue badge" },
  { key: "loading_bay", label: "Loading" },
  { key: "motorcycle", label: "Moto" },
];
// "Available now" reports live for a short, fixed window — never asked of the user.
const SPOTTED_EXPIRY_MIN = 15;

export default function QuickSpotSheet({ open, onClose, onSubmit, pos, busy }) {
  const dismissProps = useBackdropDismiss(open, onClose);
  const [type, setType] = React.useState("normal");
  const [photo, setPhoto] = React.useState(null);
  const [preview, setPreview] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    setType("normal");
    setPhoto(null);
    setPreview(null);
  }, [open]);

  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhoto(file);
    setPreview(URL.createObjectURL(file));
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2000] bg-black/70"
            {...dismissProps}
            data-testid="quick-spot-backdrop"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 280 }}
            className="fixed inset-x-0 bottom-0 z-[2001] bg-[#141416] border-t border-white/10 rounded-t-3xl p-5 pb-8 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:bottom-6 sm:rounded-3xl sm:border sm:w-[420px] sm:max-w-[92vw]"
            style={{ paddingBottom: "calc(2rem + env(safe-area-inset-bottom, 0px))" }}
            data-testid="quick-spot-sheet"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#22d3ee] font-semibold">
                  Free spot spotted
                </div>
                <div className="font-heading text-xl font-semibold text-white mt-1 leading-tight">
                  Post it at your location
                </div>
                <p className="text-[12px] text-zinc-400 mt-1 leading-snug">
                  {pos
                    ? `Using your GPS position (${pos.lat.toFixed(4)}, ${pos.lng.toFixed(4)})`
                    : "Waiting for your location…"}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-95 transition-transform"
                data-testid="quick-spot-close"
                aria-label="Close"
              >
                <X className="w-4 h-4 text-zinc-400" />
              </button>
            </div>

            <div className="mt-4 flex flex-wrap gap-1.5" data-testid="quick-spot-types">
              {TYPES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setType(t.key)}
                  className={`h-9 px-3 rounded-full text-[11px] font-medium border transition-colors ${
                    type === t.key
                      ? "bg-[#ccff00] text-[#0a0a0c] border-[#ccff00]"
                      : "bg-[#0a0a0c] text-zinc-300 border-white/10"
                  }`}
                  data-testid={`quick-spot-type-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <label
              className="mt-4 flex items-center gap-3 p-2.5 rounded-2xl bg-[#0a0a0c] border border-dashed border-white/15 cursor-pointer"
              data-testid="quick-spot-photo-label"
            >
              {preview ? (
                <img src={preview} alt="Preview" className="w-10 h-10 rounded-xl object-cover" />
              ) : (
                <span className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                  <Camera className="w-4 h-4 text-zinc-400" />
                </span>
              )}
              <span className="text-[12px] text-zinc-400">
                {photo ? "Photo added — tap to change" : "Add a photo (optional)"}
              </span>
              <input
                type="file"
                accept="image/*"
                onChange={pickPhoto}
                className="hidden"
                data-testid="quick-spot-photo-input"
              />
            </label>

            <button
              type="button"
              onClick={() => onSubmit({ parking_type: type, duration_min: SPOTTED_EXPIRY_MIN, photo })}
              disabled={busy || !pos}
              className="mt-4 w-full h-12 rounded-full bg-[#ccff00] text-[#0a0a0c] font-semibold text-sm inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60"
              data-testid="quick-spot-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
              Post spot
            </button>
            <p className="mt-2 text-[10px] text-zinc-500 text-center">
              Available now — no handover needed, first driver there takes it.
            </p>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
